export const hasValue = (value) =>
  value !== null &&
  value !== undefined &&
  !(typeof value === "string" && value.trim() === "");

export const finiteNumber = (value) => {
  if (!hasValue(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatCount = (value, fallback = "Unknown") => {
  const parsed = finiteNumber(value);
  return parsed === null ? fallback : parsed.toLocaleString("en-US");
};

export const formatPercent = (value, fallback = "Unknown") => {
  const parsed = finiteNumber(value);
  if (parsed === null) return fallback;
  const percent = parsed >= 0 && parsed <= 1 ? parsed * 100 : parsed;
  return `${percent.toLocaleString("en-US", {
    minimumFractionDigits: percent < 100 ? 1 : 0,
    maximumFractionDigits: 1,
  })}%`;
};

export const formatDuration = (milliseconds, fallback = "Unknown") => {
  const value = finiteNumber(milliseconds);
  if (value === null) return fallback;
  if (value < 1000) return `${Math.round(value)} ms`;
  if (value < 60_000) return `${(value / 1000).toFixed(value < 10_000 ? 1 : 0)} sec`;
  const minutes = Math.floor(value / 60_000);
  const seconds = Math.round((value % 60_000) / 1000);
  return `${minutes} min${seconds ? ` ${seconds} sec` : ""}`;
};

// The manager service and PostgreSQL use UTC, but LocalDateTime values are
// serialized without an offset. Treat only those complete, offset-less date
// times as UTC; preserve explicit offsets and Date instances as supplied.
export const parseSupportDate = (value) => {
  if (value instanceof Date) return value;
  if (typeof value !== "string") return new Date(value);

  const trimmed = value.trim();
  const offsetLessDateTime =
    /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;
  const normalized = offsetLessDateTime.test(trimmed)
    ? `${trimmed.replace(" ", "T")}Z`
    : trimmed;

  return new Date(normalized);
};

export const formatDateTime = (value, fallback = "Not recorded") => {
  if (!hasValue(value)) return fallback;
  const parsed = parseSupportDate(value);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
};

export const formatRelativeTime = (value, fallback = "Not recorded") => {
  if (!hasValue(value)) return fallback;
  const parsed = parseSupportDate(value);
  if (Number.isNaN(parsed.getTime())) return fallback;
  const seconds = Math.round((parsed.getTime() - Date.now()) / 1000);
  const absolute = Math.abs(seconds);
  const formatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (absolute < 60) return formatter.format(seconds, "second");
  if (absolute < 3600) return formatter.format(Math.round(seconds / 60), "minute");
  if (absolute < 86_400) return formatter.format(Math.round(seconds / 3600), "hour");
  return formatter.format(Math.round(seconds / 86_400), "day");
};

export const readableStatus = (value, fallback = "Unknown") => {
  if (!hasValue(value)) return fallback;
  return String(value)
    .trim()
    .toLowerCase()
    .split(/[_\s-]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export const statusTone = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (
    [
      "HEALTHY",
      "ACTIVE",
      "CONNECTED",
      "READY",
      "SUCCESS",
      "SUCCEEDED",
      "SYNCED",
      "PROCESSED",
      "COMPLETED",
      "SENT",
      "UP",
      "LIVE",
      "PROSPECTIVE_LIVE",
      "RESOLVED_SELLER_FAVOUR",
    ].includes(normalized)
  ) {
    return "success";
  }
  if (
    [
      "DEGRADED",
      "WARNING",
      "TRIAL",
      "RETRY_WAIT",
      "RETRYING",
      "PENDING",
      "STALE",
      "INACTIVE",
      "READ_ONLY",
      "DRY_RUN",
      "SUPPRESSED",
      "TEST_CANARY",
      "UNTRACKED",
      "NOT_STARTED",
      "IN_PROGRESS",
      "DEFERRED",
      "NEEDS_ATTENTION",
      "SYNCING",
      "RATE_LIMITED",
      "TOKEN_REFRESH_REQUIRED",
      "UNDER_REVIEW",
    ].includes(normalized)
  ) {
    return "warning";
  }
  if (
    [
      "ATTENTION",
      "ACTION_REQUIRED",
      "CRITICAL",
      "FAILED",
      "DOWN",
      "DISABLED",
      "BLOCKED",
      "DISCONNECTED",
      "ERROR",
      "MISCONFIGURED",
      "WRITER_UNAVAILABLE",
      "WRITER_UNVERIFIED",
      "DISPUTE_SYNC_ERROR",
      "DISPUTE_SYNC_STALLED",
      "TOKEN_REFRESH_STALLED",
      "CREDENTIAL_MISSING",
      "WAITING_FOR_SELLER_RESPONSE",
      "RESOLVED_BUYER_FAVOUR",
    ].includes(normalized)
  ) {
    return "critical";
  }
  return "info";
};

export const storeId = (store) =>
  store?.id ?? store?.shopId ?? store?.storeId ?? store?.externalId ?? null;

export const storeName = (store) =>
  store?.name || store?.storeName || store?.shopName || "Unnamed store";

export const storeDomain = (store) =>
  store?.myShopifyDomain || store?.domain || store?.shopDomain || "Domain not recorded";

export const storeHealth = (store) =>
  store?.health || store?.healthStatus || store?.status || "UNKNOWN";

export const storeBilling = (store) =>
  store?.billing?.status ||
  store?.billingStatus ||
  store?.subscriptionStatus ||
  (store?.trial === true || store?.onTrial === true ? "TRIAL" : "UNKNOWN");

export const storePipeline = (store) => store?.pipeline || store?.sync || store || {};

export const merchantScreenLabel = (onboarding = {}, fallback = "Unknown") => {
  const status = String(onboarding?.status || "").toUpperCase();
  const screen = String(onboarding?.merchantScreen || "").toUpperCase();
  const guideVisible = onboarding?.guideVisible ?? onboarding?.showGuide;
  if (status === "UNTRACKED" || screen === "LEGACY_DASHBOARD") {
    return "Legacy dashboard / onboarding not tracked";
  }
  if (status === "NEEDS_ATTENTION" && screen === "ONBOARDING") {
    return "Onboarding · merchant action required";
  }
  if (status === "IN_PROGRESS" || screen === "ONBOARDING") {
    return `Onboarding · step ${onboarding?.currentStep || 1} of 3`;
  }
  if (status === "COMPLETED" && screen === "DASHBOARD") {
    return guideVisible
      ? "Dashboard with completed setup guide"
      : "Standard dashboard";
  }
  if (status === "DEFERRED" && screen === "DASHBOARD") {
    return "Dashboard with setup guide";
  }
  if (screen === "DASHBOARD") return "Standard dashboard";
  return readableStatus(onboarding?.merchantScreen, fallback);
};

export const firstArray = (...values) =>
  values.find((value) => Array.isArray(value)) || [];
